import { useState, useEffect } from "react";
import { Session, User, AuthChangeEvent } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  lastAuthEvent: AuthChangeEvent | null;
  inRecovery: boolean;
}

export function useAuthState(): AuthState & {
  setIsLoading: (value: boolean) => void;
} {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastAuthEvent, setLastAuthEvent] = useState<AuthChangeEvent | null>(
    null,
  );

  // Check URL hash for recovery tokens BEFORE Supabase processes them
  const initialRecoveryState = () => {
    if (typeof window !== "undefined") {
      const hash = window.location.hash;
      return hash.includes("access_token") && hash.includes("type=recovery");
    }
    return false;
  };

  const [inRecovery, setInRecovery] = useState(initialRecoveryState);

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Track recovery state based on auth events
      if (event === "PASSWORD_RECOVERY") {
        console.log("[useAuthState] Password recovery session detected");
        setInRecovery(true);
      }
      if (event === "USER_UPDATED") {
        console.log("[useAuthState] User updated - clearing recovery state");
        setInRecovery(false);
      }
      if (event === "SIGNED_OUT") {
        console.log("[useAuthState] User signed out - clearing recovery state");
        setInRecovery(false);
      }

      // Handle INITIAL_SESSION case where recovery tokens were processed
      if (
        event === "INITIAL_SESSION" &&
        session &&
        typeof window !== "undefined"
      ) {
        const hash = window.location.hash;
        if (hash.includes("access_token") && hash.includes("type=recovery")) {
          console.log(
            "[useAuthState] Initial session with recovery tokens detected",
          );
          setInRecovery(true);
        }
      }

      setSession(session);
      setUser(session?.user ?? null);
      setLastAuthEvent(event);

      // Only set loading to false after initial check or on specific events
      if (
        event === "INITIAL_SESSION" ||
        event === "SIGNED_IN" ||
        event === "SIGNED_OUT"
      ) {
        setIsLoading(false);
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { user, session, isLoading, setIsLoading, lastAuthEvent, inRecovery };
}
